import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  IAssignableUser,
  ICertificationFilterOption,
  ICertificationsDataCtx,
  IFilterItem,
  IGetUsersParams,
} from 'contexts/certifications/types';
import Notification from 'components/notification';
import { getInvitations } from 'services/users';
import {
  assignVouchersAction,
  getCertificationFilterOptionsAction,
  getAssignableUsersAction,
  getCertificationsCountersAction,
  getUsersCertificationsLogsAction,
  getVoucherSummaryAction,
} from 'contexts/certifications/actions';
import { cloneDeep, isEmpty } from 'lodash';
import { certificationsInitialValues } from 'contexts/certifications/utils';
import { SessionContext } from 'contexts/session';
import { CapabilitiesContext } from 'contexts/capabilities';
import { VoucherSummary } from 'contexts/voucher/types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { loadSortState, toOrdering } from 'utils/sorting';

interface CertificationsContentHook {
  fetching: boolean;
  fetchingCounters: boolean;
  fetchingVoucherSummary: boolean;
  fetchingUsers: boolean;
  fetchingTable: boolean;
  fetchingCertificationOptions: boolean;
  isFirstFetching: boolean;
  certificationOptions: ICertificationFilterOption[];
  filters: ICertificationsDataCtx['filters'];
  counters: ICertificationsDataCtx['counters'];
  voucherSummary: VoucherSummary | null;
  users: any;
  usersTable: any;
  usersCount: number;
  emptyState: boolean;
  changeFilters: (newValue: IFilterItem) => void;
  getUsers: (data: IGetUsersParams) => void;
  loadingMoreUsers: boolean;
  refreshVoucherData: () => Promise<void>;
  assignableUsers: IAssignableUser[];
  assignableUsersCount: number;
  fetchingAssignableUsers: boolean;
  getAssignableUsers: (params: {
    search?: string;
    certification_version_ids?: string[];
  }) => Promise<void>;
  assignVouchers: (
    assignments: Array<{ certification_version_id: string; assignee_user_id: string }>
  ) => Promise<{
    success: boolean;
    totalAssigned: number;
    totalFailed: number;
    failedCertificationIds: string[];
    rejectedUserIds: string[];
    rejectedReasons: Array<{ userId: string; certificationId: string; reason: string }>;
    failedAssignments: Record<string, string[]>;
  }>;
}

interface GetInvitationsParams {
  active?: string;
  license?: string | null;
  teams?: string;
  roles?: string | null;
  page?: number;
  page_size?: number;
  search?: string;
}

const useCertificationsContent = (): CertificationsContentHook => {
  const { user, account, isTeamManager } = useContext(SessionContext);
  const certificationSortStorageKey = { table: 'certifications', context: account?.id };
  const [filters, setFilters] = useState<ICertificationsDataCtx['filters']>({
    ...certificationsInitialValues.filters,
    ordering: toOrdering(loadSortState(certificationSortStorageKey)),
  });
  const [counters, setCounters] = useState(certificationsInitialValues.counters);
  const [voucherSummary, setVoucherSummary] = useState<VoucherSummary | null>(
    certificationsInitialValues.voucherSummary
  );
  const [fetching, setFetching] = useState(true);
  const [fetchingCounters, setFetchingCounters] = useState(false);
  const [fetchingVoucherSummary, setFetchingVoucherSummary] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [fetchingTable, setFetchingTable] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [usersTable, setUsersTable] = useState<any[]>([]);
  const [usersCount, setUsersCount] = useState(0);
  const [isFirstFetching, setIsFirstFetching] = useState(true);
  const [usersData, setUsersData] = useState(null);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState<IAssignableUser[]>([]);
  const [assignableUsersCount, setAssignableUsersCount] = useState(0);
  const [fetchingAssignableUsers, setFetchingAssignableUsers] = useState(false);
  const [certificationOptions, setCertificationOptions] = useState<ICertificationFilterOption[]>(
    []
  );
  const [fetchingCertificationOptions, setFetchingCertificationOptions] = useState(false);
  const voucherAssignmentV1 = useFlags()?.voucherAssignmentV1 ?? false;
  const capabilities = useContext(CapabilitiesContext);
  const canViewCertificationInsights = capabilities?.canViewCertificationInsights ?? true;

  const getUsers = async (data: IGetUsersParams): Promise<any[]> => {
    if (!canViewCertificationInsights) return users;

    const pageSize = 40;
    setFetchingUsers(true);

    const teams = !isTeamManager
      ? filters?.teams.map((team) => team.slug).join(',')
      : currentTeamSlugs;
    try {
      const { load_more, query } = data;
      let nextParams = null;
      if (load_more) {
        const urlParams =
          load_more && usersData && usersData?.next ? parseURLToPayload(usersData.next) : {};

        const maxPages = Math.ceil(usersData?.count / pageSize);
        const nextPage = urlParams?.page;
        const currentPage = usersData?.page || 1;
        if (currentPage === maxPages) return;
        nextParams = currentPage !== nextPage && nextPage <= maxPages ? urlParams : {};
        setLoadingMoreUsers(true);
      }

      const payload = {
        active: 'true',
        license: null,
        teams,
        roles: null,
        page: 1,
        page_size: pageSize,
        search: query || '',
        ...nextParams,
      };

      const response = await getInvitations(payload);

      if (response.status === 200) {
        const newUsers = response.data.results.map((u) => ({
          email: u.email,
          id: u.uaa_id,
          name: `${u.uaa_data?.profile?.data?.first_name ?? ''} ${
            u.uaa_data?.profile?.data?.last_name ?? ''
          }`.trim(),
        }));

        const updatedUsers = load_more ? [...users, ...newUsers] : newUsers;

        setUsers(updatedUsers);
        setUsersData({ ...response.data, page: payload.page });

        return newUsers;
      }

      return users;
    } catch (error) {
      Notification({ text: 'There has been a problem fetching users', type: 'error' });
      return [];
    } finally {
      setFetchingUsers(false);
      setLoadingMoreUsers(false);
    }
  };

  const getUsersCertificationsLogs = async (resetUsers = false, isFirstRender = false) => {
    if (!canViewCertificationInsights) return;

    try {
      setFetchingTable(true);
      setIsFirstFetching(isFirstRender);
      const {
        certification_short_names,
        result,
        user_ids,
        expiration_date_range,
        assigned_date_range,
        page,
        page_size,
        teams,
        ordering,
      } = filters;
      const userIds = resetUsers ? [] : user_ids;
      const response = await getUsersCertificationsLogsAction(
        {
          certification_short_names,
          result,
          user_ids: userIds,
          teams,
          expiration_date_range,
          assigned_date_range,
          page,
          page_size,
          ordering,
        },
        account?.id,
        currentTeamIds
      );

      if (response instanceof Error) throw response;

      setUsersTable(response.results);
      setUsersCount(response.count);
    } catch (error) {
      Notification({
        text: 'There has been a problem fetching users certifications logs',
        type: 'error',
      });
    } finally {
      setFetchingTable(false);
    }
  };

  const fetchAllData = async (isFirstRender = false) => {
    setFetching(true);
    setIsFirstFetching(isFirstRender);
    const resetUsers = true;
    await Promise.allSettled([
      getUsers({}),
      getUsersCertificationsLogs(resetUsers, isFirstRender),
      getVoucherSummary(),
      getCounters(),
    ]);
    setFetching(false);
  };

  const { teams, ...otherFilters } = filters;

  const memorizedTeams = useMemo(() => teams, [JSON.stringify(teams)]);
  const memorizedOtherFilters = useMemo(() => otherFilters, [JSON.stringify(otherFilters)]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    setFilters({ ...filters, user_ids: [] });
    if (!isFirstRender.current) fetchAllData();
  }, [memorizedTeams, voucherAssignmentV1]);

  useEffect(() => {
    if (isFirstRender.current) {
      fetchAllData(true);
      fetchCertificationOptions();
      isFirstRender.current = false;
    } else {
      getUsersCertificationsLogs();
    }
  }, [memorizedOtherFilters]);

  const fetchCertificationOptions = async () => {
    if (!account?.id || !canViewCertificationInsights) return;
    setFetchingCertificationOptions(true);
    try {
      const response = await getCertificationFilterOptionsAction(account.id);
      if (!Array.isArray(response)) throw new Error('Failed to load certification filter options');
      setCertificationOptions(response);
    } catch (error) {
      Notification({
        text: 'There has been a problem fetching certification filter options',
        type: 'error',
      });
    } finally {
      setFetchingCertificationOptions(false);
    }
  };

  const getCounters = async () => {
    if (!canViewCertificationInsights) return;

    setFetchingCounters(true);
    try {
      const payload = {
        account_id: account?.id,
        team_ids: currentTeamIds,
      };
      const response = await getCertificationsCountersAction(payload);
      if (response instanceof Error) throw response;
      setCounters(response.results);
    } catch (error) {
      Notification({ text: 'There has been a problem fetching counters', type: 'error' });
    } finally {
      setFetchingCounters(false);
    }
  };

  const getVoucherSummary = async () => {
    if (voucherAssignmentV1 === false) {
      return;
    }

    setFetchingVoucherSummary(true);
    try {
      const response = await getVoucherSummaryAction({ account_id: account?.id });
      if (response instanceof Error) throw response;
      setVoucherSummary(response.results);
    } catch (error) {
      Notification({
        text: `There has been a problem fetching voucher summary: ${error?.message || error}`,
        type: 'error',
      });
    } finally {
      setFetchingVoucherSummary(false);
    }
  };

  const changeFilters = (newValue: IFilterItem) => {
    if (isEmpty(newValue.teams)) {
      const newValueClone = cloneDeep(newValue);
      const teams = [];
      user.teams.forEach((team) => team.is_manager && teams.push(team));
      newValueClone.teams = teams;
      setFilters(newValueClone);
    } else {
      setFilters(newValue);
    }
  };

  const teamManagerTeamIds = useMemo(() => {
    if (!isTeamManager) return [];
    return user.teams.filter((team) => team?.is_manager).map((team) => team.team_id);
  }, [user]);

  const currentTeamIds = useMemo(() => {
    if (filters.teams.length) {
      return filters.teams.map((team) => team.id || team.team_id);
    } else {
      return teamManagerTeamIds;
    }
  }, [memorizedTeams]);

  const currentTeamSlugs = useMemo(() => {
    if (!filters.teams.length) return '';
    return filters.teams
      .map((team) => team.slug)
      .filter(Boolean)
      .join(',');
  }, [memorizedTeams]);

  const isEveryFilterEmpty = useMemo(() => {
    const { certification_short_names, expiration_date_range, teams, user_ids } = filters;
    return [certification_short_names, expiration_date_range, teams, user_ids].every(isEmpty);
  }, [filters]);

  const emptyState = useMemo(() => {
    const hasEnabledExams =
      voucherSummary &&
      voucherSummary.voucher_balance.total_purchased > 0 &&
      voucherSummary.exams.some((exam) => exam.is_enabled);
    return (
      !fetchingTable &&
      isEmpty(usersTable) &&
      isEveryFilterEmpty &&
      isFirstFetching &&
      !hasEnabledExams
    );
  }, [fetchingTable, usersTable, isEveryFilterEmpty, isFirstFetching, voucherSummary]);

  const parseURLToPayload = (url: string): GetInvitationsParams => {
    const params = new URLSearchParams(url.split('?')[1]);

    const payload: GetInvitationsParams = {};

    if (params.has('active')) {
      payload.active = params.get('active')!;
    }

    if (params.has('teams')) {
      payload.teams = params.get('teams')!;
    }

    if (params.has('page')) {
      payload.page = parseInt(params.get('page')!, 10);
    }

    if (params.has('page_size')) {
      payload.page_size = parseInt(params.get('page_size')!, 10);
    }

    if (params.has('search')) {
      payload.search = params.get('search')!;
    }

    return payload;
  };

  const refreshVoucherData = useCallback(async () => {
    await Promise.allSettled([getCounters(), getVoucherSummary(), getUsersCertificationsLogs()]);
  }, [getCounters, getVoucherSummary, getUsersCertificationsLogs]);

  const getAssignableUsers = useCallback(
    async (
      params: { search?: string; certification_version_ids?: string[] } = {}
    ): Promise<void> => {
      if (voucherAssignmentV1 === false) {
        return;
      }

      setFetchingAssignableUsers(true);
      try {
        const certVersionIds = params.certification_version_ids || [];
        if (certVersionIds.length === 0) {
          setAssignableUsers([]);
          setAssignableUsersCount(0);
          setFetchingAssignableUsers(false);
          return;
        }

        const response = await getAssignableUsersAction({
          account_id: account?.id,
          certification_version_ids: certVersionIds,
          team_ids: currentTeamIds,
          search: params.search,
        });

        if (response instanceof Error) throw response;

        setAssignableUsers(response.results);
        setAssignableUsersCount(response.count);
      } catch (error) {
        Notification({ text: 'Failed to fetch assignable users', type: 'error' });
        setAssignableUsers([]);
        setAssignableUsersCount(0);
      } finally {
        setFetchingAssignableUsers(false);
      }
    },
    [account?.id, currentTeamIds, voucherAssignmentV1]
  );

  const assignVouchers = async (
    assignments: Array<{ certification_version_id: string; assignee_user_id: string }>
  ): Promise<{
    success: boolean;
    totalAssigned: number;
    totalFailed: number;
    failedCertificationIds: string[];
    rejectedUserIds: string[];
    rejectedReasons: Array<{ userId: string; certificationId: string; reason: string }>;
    failedAssignments: Record<string, string[]>;
  }> => {
    if (voucherAssignmentV1 === false) {
      throw new Error('Voucher assignment feature is disabled.');
    }
    const response = await assignVouchersAction({
      account_id: account?.id,
      assignments,
    });

    if (response instanceof Error) {
      throw response;
    }

    refreshVoucherData();

    return {
      success: response.success,
      totalAssigned: response.totalAssigned,
      totalFailed: response.totalFailed,
      failedCertificationIds: response.failedCertificationIds,
      rejectedUserIds: response.rejectedUserIds,
      rejectedReasons: response.rejectedReasons,
      failedAssignments: response.failedAssignments,
    };
  };

  useEffect(() => {
    const handleRefresh = () => {
      refreshVoucherData();
    };

    window.addEventListener('refreshVoucherData', handleRefresh);
    return () => window.removeEventListener('refreshVoucherData', handleRefresh);
  }, [refreshVoucherData]);

  return {
    fetching,
    fetchingCounters,
    fetchingVoucherSummary,
    fetchingUsers,
    fetchingTable,
    fetchingCertificationOptions,
    isFirstFetching,
    certificationOptions,
    filters,
    users,
    usersTable,
    usersCount,
    counters,
    voucherSummary,
    emptyState,
    changeFilters,
    getUsers,
    loadingMoreUsers,
    refreshVoucherData,
    assignableUsers,
    assignableUsersCount,
    fetchingAssignableUsers,
    getAssignableUsers,
    assignVouchers,
  };
};

export default useCertificationsContent;
